import { useState } from 'react';
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  RefreshCw,
  Clock,
  Info,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  type Notification,
} from '../hooks/useNotifications';
import { Card, Button, cn } from '@rhpc/ui';
import { PageHeader, PageShell } from '@/components/PageShell';

type FilterStatus = 'all' | 'unread' | 'read';

const KIND_STYLE: Record<string, { bg: string; fg: string; border: string; Icon: typeof Clock }> = {
  CLAIM_APPROVED: { bg: 'bg-ok-soft', fg: 'text-ok', border: 'border-l-ok', Icon: CheckCircle2 },
  CLAIM_HR_APPROVED: { bg: 'bg-ok-soft', fg: 'text-ok', border: 'border-l-ok', Icon: CheckCircle2 },
  CLAIM_ADMIN_APPROVED: { bg: 'bg-info-soft', fg: 'text-info', border: 'border-l-info', Icon: CheckCircle2 },
  CLAIM_FINANCE_APPROVED: { bg: 'bg-info-soft', fg: 'text-info', border: 'border-l-info', Icon: CheckCircle2 },
  CLAIM_RETURNED: { bg: 'bg-warn-soft', fg: 'text-warn', border: 'border-l-warn', Icon: AlertTriangle },
  CLAIM_REJECTED: { bg: 'bg-danger-soft', fg: 'text-danger', border: 'border-l-danger', Icon: XCircle },
  CLAIM_SUBMITTED: { bg: 'bg-info-soft', fg: 'text-info', border: 'border-l-info', Icon: Info },
};
const DEFAULT_KIND_STYLE = { bg: 'bg-surface-2', fg: 'text-text-3', border: 'border-l-text-3', Icon: Clock };

function kindStyleFor(type: string) {
  return KIND_STYLE[type] ?? DEFAULT_KIND_STYLE;
}

function formatTimestamp(iso: string) {
  return new Date(iso).toLocaleString('en-MY', {
    timeZone: 'Asia/Kuala_Lumpur',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function NotificationsSkeleton() {
  return (
    <Card className="overflow-hidden">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex gap-3.5 border-b border-border p-[18px_22px] last:border-b-0">
          <div className="skeleton h-9 w-9 shrink-0 rounded-full" />
          <div className="flex-1">
            <div className="skeleton h-[13px] w-3/5 rounded" />
            <div className="skeleton mt-2 h-[11px] w-2/5 rounded" />
          </div>
        </div>
      ))}
    </Card>
  );
}

function NotificationsError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center rounded-lg border border-border bg-surface px-6 py-12 text-center shadow-[0_1px_2px_0_rgb(15_23_42_/_0.05)]">
      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-danger-soft text-danger">
        <AlertTriangle className="h-6 w-6" strokeWidth={2} />
      </div>
      <h2 className="text-lg font-semibold">Couldn't load notifications</h2>
      <p className="mx-auto mt-2 max-w-[420px] text-sm leading-relaxed text-text-2">
        Something went wrong while fetching your notifications. Check your connection and try again — if it
        keeps happening, contact IT support.
      </p>
      <div className="mt-6 flex gap-2.5">
        <Button onClick={onRetry} className="gap-2">
          <RefreshCw className="h-4 w-4" strokeWidth={2} />
          Try again
        </Button>
        <Button variant="outline">Contact IT</Button>
      </div>
    </div>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center rounded-lg border-[1.5px] border-dashed border-border bg-surface px-6 py-12 text-center">
      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-surface-2 text-text-2">
        <Bell className="h-6 w-6" strokeWidth={2} />
      </div>
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="mx-auto mt-2 max-w-[360px] text-sm leading-relaxed text-text-2">{description}</p>
    </div>
  );
}

function NotificationRow({ notif, onClick }: { notif: Notification; onClick: () => void }) {
  const { bg, fg, border, Icon } = kindStyleFor(notif.type);
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 border-b border-l-4 border-border p-4 text-left transition-colors last:border-b-0 hover:bg-surface-2/50 sm:gap-3.5 sm:p-[16px_22px]',
        !notif.isRead ? cn('bg-surface-2/40', border) : 'border-l-transparent'
      )}
    >
      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', bg, fg)}>
        <Icon className="h-[17px] w-[17px]" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className={cn('text-sm', notif.isRead ? 'font-medium' : 'font-bold')}>{notif.title}</span>
            {!notif.isRead && (
              <span className="inline-flex items-center rounded-full bg-info-soft px-2 py-[1px] text-[10px] font-bold uppercase tracking-wide text-info">
                New
              </span>
            )}
          </div>
          <p className="shrink-0 text-xs text-text-2 sm:whitespace-nowrap">
            {formatTimestamp(notif.createdAt)}
          </p>
        </div>
        <p className="mt-[3px] text-[13px] text-text-2">{notif.message}</p>
      </div>
    </button>
  );
}

export function NotificationsPage() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<FilterStatus>('all');

  const { data, isLoading, isError, refetch } = useNotifications(page, 20);
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const allNotifications = data?.notifications ?? [];
  const notifications = allNotifications.filter((n) => {
    if (filter === 'unread') return !n.isRead;
    if (filter === 'read') return n.isRead;
    return true;
  });

  const totalPages = data?.meta.totalPages ?? 1;
  const unreadCount = data?.unreadCount ?? 0;

  const emptyCopy =
    filter === 'unread'
      ? { title: "You're all caught up", description: 'No unread notifications right now.' }
      : filter === 'read'
        ? { title: 'No read notifications', description: "You haven't read any notifications yet." }
        : { title: 'No notifications', description: "Nothing to show here yet. We'll let you know when there's an update on one of your claims." };

  return (
    <PageShell fill>
      <div className="shrink-0">
        <PageHeader
          title="Notifications"
          description={
            unreadCount > 0
              ? `You have ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
              : 'All caught up'
          }
          actions={
            unreadCount > 0 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAllAsRead.mutate()}
                disabled={markAllAsRead.isPending}
                className="w-full sm:w-auto"
              >
                Mark all as read
              </Button>
            ) : undefined
          }
        />

        {/* Tabs */}
        <div className="mb-4 inline-flex gap-0.5 rounded-lg bg-surface-2 p-[3px]">
          {(['all', 'unread', 'read'] as FilterStatus[]).map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilter(f);
                setPage(1);
              }}
              className={cn(
                'h-8 rounded-md px-4 text-[13px] font-semibold capitalize transition-colors',
                filter === f
                  ? 'bg-surface text-text shadow-[0_1px_2px_0_rgb(15_23_42_/_0.08)]'
                  : 'text-text-2 hover:text-text'
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && <NotificationsSkeleton />}

        {!isLoading && isError && <NotificationsError onRetry={() => refetch()} />}

        {!isLoading && !isError && allNotifications.length === 0 && (
          <EmptyState
            title="No notifications"
            description="Nothing to show here yet. We'll let you know when there's an update on one of your claims."
          />
        )}

        {!isLoading && !isError && allNotifications.length > 0 && notifications.length === 0 && (
          <EmptyState title={emptyCopy.title} description={emptyCopy.description} />
        )}

        {!isLoading && !isError && notifications.length > 0 && (
          <Card className="overflow-hidden">
            {notifications.map((notif) => (
              <NotificationRow
                key={notif.id}
                notif={notif}
                onClick={() => {
                  if (!notif.isRead) markAsRead.mutate(notif.id);
                }}
              />
            ))}
          </Card>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex shrink-0 items-center justify-between border-t border-border pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="flex items-center gap-1 text-sm font-medium text-text-2 transition-colors hover:text-text disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            Previous
          </button>
          <span className="text-sm text-text-2">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="flex items-center gap-1 text-sm font-medium text-text-2 transition-colors hover:text-text disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
            <ChevronRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      )}
    </PageShell>
  );
}
